import Decimal from 'break_infinity.js';
import { GENERATOR_COUNT, GENERATORS } from '../data/generators.ts';
import {
  CLOCK_DEFAULT,
  DEFAULT_AUTOMELT_THRESHOLD,
  HEAT_MIN,
} from '../data/balance.ts';

export type NumberFormat = 'standard' | 'scientific';

export interface GameStats {
  lifetimeThoughts: Decimal;
  meltdowns: number;
  bestRunThoughts: Decimal;
  /** Highest heat reached without melting down. */
  hottestSurvived: number;
  fastestMeltdownSec: number;
  totalPlaytimeSec: number;
  firstPlayedAt: number;
}

export interface GameSettings {
  numberFormat: NumberFormat;
  /** null = follow the OS preference. */
  reducedMotion: boolean | null;
  autoMeltdownEnabled: boolean;
  autoMeltdownThreshold: number;
  autoBuy: Record<string, boolean>;
}

/**
 * The entire game. One plain, serializable object.
 *
 * No classes with methods, no Map, no Set, no functions. Everything here must
 * survive JSON.stringify -> JSON.parse, with Decimal values written as strings
 * and rehydrated on load (see save/save.ts).
 */
export interface GameState {
  /** Save schema version. See save/migrate.ts. */
  version: number;

  // --- Current run -------------------------------------------------------
  thoughts: Decimal;
  /** Lifetime thoughts for this run only. Drives the meltdown payout. */
  thoughtsThisRun: Decimal;
  /** Owned count per generator, indexed to match GENERATORS. */
  generators: number[];
  /** Percent. CLOCK_MIN..clockCap. */
  clock: number;
  /** 0..100. */
  heat: number;
  /** Highest heat this run reached. Drives the cracks, which read as history. */
  peakHeatThisRun: number;
  /** Seconds remaining until Vent Coolant is ready. 0 = ready. */
  ventCooldown: number;
  /** Seconds elapsed in the current run. */
  runTimeSec: number;

  // --- Persistent across meltdown ---------------------------------------
  /**
   * Ideas: the permanent currency, shown to the player as "ideas".
   *
   * The field keeps its original name so existing saves load without a
   * migration. Rename it only alongside a save version bump.
   */
  greyMatter: Decimal;
  /**
   * Fraction of an idea carried over from previous meltdowns, 0 to just under 1.
   *
   * The payout is floored to whole ideas, and without this the remainder was
   * simply destroyed. That made every run too short to be worth a whole idea
   * pay exactly nothing, which punished the fast, hot runs the clock exists to
   * make possible. Progress is now never lost, only banked in pieces.
   */
  ideaRemainder: Decimal;
  /** skillId -> rank owned. Absent or 0 means unowned. */
  skills: Record<string, number>;

  // --- Never reset -------------------------------------------------------
  stats: GameStats;
  /** Feature flags earned. Never revoked. */
  unlocks: Record<string, boolean>;
  settings: GameSettings;

  /**
   * Hook for the Intrusive Thoughts expansion (v1.x). Always empty in v1.
   * Present so the save schema does not need a migration to gain it.
   */
  entities: unknown[];

  /** Epoch ms of the last save. Drives the offline calculation. */
  lastSavedAt: number;
}

export function createNewState(now: number): GameState {
  return {
    version: 1,

    thoughts: new Decimal(0),
    thoughtsThisRun: new Decimal(0),
    generators: new Array<number>(GENERATOR_COUNT).fill(0),
    clock: CLOCK_DEFAULT,
    heat: HEAT_MIN,
    peakHeatThisRun: HEAT_MIN,
    ventCooldown: 0,
    runTimeSec: 0,

    greyMatter: new Decimal(0),
    ideaRemainder: new Decimal(0),
    skills: {},

    stats: {
      lifetimeThoughts: new Decimal(0),
      meltdowns: 0,
      bestRunThoughts: new Decimal(0),
      hottestSurvived: 0,
      fastestMeltdownSec: 0,
      totalPlaytimeSec: 0,
      firstPlayedAt: now,
    },
    // The first two tiers are visible immediately. An empty generator column
    // on a fresh save reads as broken, and eight locked rows reads as a wall.
    unlocks: Object.fromEntries(
      GENERATORS.slice(0, 2).map((def) => [`gen:${def.id}`, true]),
    ),
    settings: {
      numberFormat: 'standard',
      reducedMotion: null,
      autoMeltdownEnabled: false,
      autoMeltdownThreshold: DEFAULT_AUTOMELT_THRESHOLD,
      autoBuy: {},
    },

    entities: [],

    lastSavedAt: now,
  };
}
